import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import classNames from "classnames"

interface RoleBadgeProps {
  role: IGameRole
  playerOrder: number
  // Overrides role.effect for the tooltip card — some roles' numbers depend on
  // the map (e.g. private-buyer's purchase count, financer's starting bonus),
  // so the caller computes the map-correct copy instead of this component
  // knowing about maps.
  effect?: string
  // A number that belongs to the role and has to stay readable at a glance --
  // private buyer's remaining no-auction purchases. It renders *inside* the
  // pill rather than as a badge beside it: a sibling badge is a second flex
  // child, and in the narrow sidebar column it wrapped onto a line of its own,
  // leaving that one player's card a whole line taller than everybody else's.
  count?: { icon: string; value: number }
}

// Gap between the badge and the edge of the tooltip card.
const GAP = 10
// Keep the card this far away from the viewport edges when clamping.
const EDGE_PADDING = 8

/**
 * A player's role badge, and the description card it reveals on hover.
 *
 * The card is rendered through a portal to document.body: the player cards live
 * in the narrow left/right sidebar columns, so a card positioned inside the DOM
 * tree would either be squeezed into the column or painted underneath the
 * board. Anchoring to the badge's viewport rect and flipping the card towards
 * the middle of the screen keeps it readable from either sidebar.
 */
const RoleBadge: React.FC<RoleBadgeProps> = ({ role, playerOrder, effect, count }) => {
  const badgeRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const [hovered, setHovered] = useState(false)
  // Tapping pins the card open — hover alone is useless on touch screens.
  const [pinned, setPinned] = useState(false)
  const [anchor, setAnchor] = useState<{ x: number; y: number; openLeft: boolean } | null>(null)
  // Vertical correction applied after measuring, so a card next to the bottom
  // player doesn't hang off the screen, plus the measured height it came from.
  const [offsetY, setOffsetY] = useState(0)
  const [cardHeight, setCardHeight] = useState(0)

  const open = hovered || pinned

  // The arrow tracks the badge as the card is clamped, but is kept inside the
  // card's rounded corners so it never floats off the edge.
  const arrowLimit = Math.max(0, cardHeight / 2 - 12)
  const arrowShift = Math.max(-arrowLimit, Math.min(offsetY, arrowLimit))

  useLayoutEffect(() => {
    if (!open) return
    const update = () => {
      if (!badgeRef.current) return
      const rect = badgeRef.current.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const openLeft = centerX > window.innerWidth / 2
      setAnchor({
        x: openLeft ? rect.left - GAP : rect.right + GAP,
        y: rect.top + rect.height / 2,
        openLeft,
      })
    }
    update()
    window.addEventListener("resize", update)
    window.addEventListener("scroll", update, true)
    return () => {
      window.removeEventListener("resize", update)
      window.removeEventListener("scroll", update, true)
    }
  }, [open])

  // Measure once positioned and nudge the card back inside the viewport. The
  // correction is derived from the card's own height rather than accumulated
  // from the previous offset, so it settles in one pass and can't oscillate
  // between the two edges when the card is taller than the viewport.
  useLayoutEffect(() => {
    if (!open || !anchor || !cardRef.current) {
      setOffsetY(0)
      return
    }
    // offsetHeight, not getBoundingClientRect: the latter is scaled down while
    // the entrance animation is still running.
    const height = cardRef.current.offsetHeight
    const desiredTop = anchor.y - height / 2
    const clampedTop = Math.max(
      EDGE_PADDING,
      Math.min(desiredTop, window.innerHeight - height - EDGE_PADDING)
    )
    setCardHeight(height)
    setOffsetY(clampedTop - desiredTop)
  }, [open, anchor])

  // A pinned card is dismissed by tapping/clicking anywhere else.
  useEffect(() => {
    if (!pinned) return
    const handlePointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement | null
      if (target && badgeRef.current?.contains(target)) return
      setPinned(false)
    }
    document.addEventListener("mousedown", handlePointerDown)
    document.addEventListener("touchstart", handlePointerDown)
    return () => {
      document.removeEventListener("mousedown", handlePointerDown)
      document.removeEventListener("touchstart", handlePointerDown)
    }
  }, [pinned])

  return (
    <>
      <div
        ref={badgeRef}
        // Bigger type on phones (below the md breakpoint), where the mobile
        // root font size shrinks the pill until the role is hard to read --
        // paid for with a tighter line box and less vertical padding, so the
        // pill ends up shorter than before despite the larger title.
        className="px-2 py-[0.22rem] text-[0.9375rem] leading-none md:py-0.5 md:text-xs md:leading-normal flex items-center gap-1.5 rounded-md font-medium font-poppins h-fit w-fit min-w-0 max-w-full whitespace-nowrap cursor-help transition-all duration-200"
        style={{
          backgroundColor: role.color + (open ? "5C" : "40"),
          color: role.color,
          boxShadow: open ? `0 0 10px ${role.color}66` : "none",
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={(e) => {
          e.stopPropagation()
          setPinned((prev) => !prev)
        }}
        data-testid={`game.player-role-badge-${playerOrder}`}
      >
        {/* The title is the only part allowed to give way. On a cramped card it
            ellipses instead of growing the pill sideways into the cards icon
            and the money sitting on the right of the player card. */}
        <span className="truncate">{role.title}</span>
        {count ? (
          <>
            <span
              className="h-3 w-px shrink-0 opacity-50"
              style={{ backgroundColor: role.color }}
            ></span>
            <span
              className="flex items-center gap-0.5 shrink-0 font-bold tabular-nums"
              data-testid={`game.player-role-count-${playerOrder}`}
            >
              <i className={classNames(count.icon, "text-[0.6875rem] md:text-[0.625rem]")}></i>
              {count.value}
            </span>
          </>
        ) : null}
      </div>

      {open && anchor
        ? createPortal(
            <div
              ref={cardRef}
              className={classNames(
                // rem, like the text inside it, so the whole card scales together
                // with the root font size instead of the text outgrowing a fixed
                // pixel box on narrow/short screens.
                "fixed z-[350] w-[17rem] max-w-[70vw] pointer-events-none role-info-card",
                { "role-info-card-left": anchor.openLeft }
              )}
              style={{ left: anchor.x, top: anchor.y + offsetY }}
            >
              <div
                className="relative rounded-xl border px-3.5 py-3 font-poppins backdrop-blur-md"
                style={{
                  borderColor: role.color + "59",
                  background:
                    "linear-gradient(150deg, rgba(24,24,27,0.97) 0%, rgba(9,9,11,0.97) 100%)",
                  boxShadow: `0 12px 32px rgba(0,0,0,0.65), 0 0 18px ${role.color}40`,
                }}
              >
                {/* Arrow, kept level with the badge even after vertical clamping. */}
                <div
                  className={classNames(
                    "absolute h-2.5 w-2.5 rotate-45",
                    anchor.openLeft ? "right-[-5px] border-r border-t" : "left-[-5px] border-l border-b"
                  )}
                  style={{
                    top: "50%",
                    marginTop: -arrowShift,
                    transform: "translateY(-50%) rotate(45deg)",
                    borderColor: role.color + "59",
                    background: "rgb(16,16,18)",
                  }}
                ></div>

                <div className="flex items-center gap-2">
                  <span
                    className="h-2 w-2 rounded-full shrink-0"
                    style={{ backgroundColor: role.color, boxShadow: `0 0 8px ${role.color}` }}
                  ></span>
                  <span
                    className="text-[1.0625rem] font-bold leading-none tracking-wide"
                    style={{ color: role.color }}
                  >
                    {role.title}
                  </span>
                </div>

                <div className="mt-2 text-[0.9375rem] leading-snug text-white/70">
                  {role.description}
                </div>

                <div
                  className="mt-2.5 flex items-start gap-1.5 rounded-md px-2 py-2 text-[0.9375rem] leading-snug font-medium"
                  style={{ backgroundColor: role.color + "1F", color: role.color }}
                >
                  <i className="fa-solid fa-bolt text-[0.75rem] mt-[3px] shrink-0"></i>
                  <span>{effect ?? role.effect}</span>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  )
}

export default React.memo(RoleBadge)
